import { useCallback, useMemo } from 'react';
import type {
  IBondScreenerViewsResponse,
  TBondScreenerType,
  TBondScreenerFiltersShape,
} from '@/creditflowresearch/domain/vo/Bondscreener';
import { useColumnsCustomizationContext } from './useColumnsCustomizationContext';
import { getVisibleIds } from './useColumnsCustomization';
import { applyBondScreenerView, buildBondScreenerViewSavePayload } from './bondScreenerViewDraft';
import useBondScreenerViewsData from './useBondScreenerViewsData';
import { MAX_BOND_SCREENER_VIEWS } from './bondScreenerViewsConstants';
import type { IApplyOptions } from './createUseBondScreenerSidebarFilter';

interface IViewsSidebarFilterContext<TFilters extends TBondScreenerFiltersShape> {
  resetTo: (filters: TFilters) => void,
  reset: () => void,
  discard: () => void,
  getDraft: () => TFilters,
  apply: (options?: IApplyOptions) => void,
}

interface ICreateUseBondScreenerViewsOptions<
  TFilters extends TBondScreenerFiltersShape,
  TApiFilters extends TBondScreenerFiltersShape,
> {
  useSidebarFilterContext: () => IViewsSidebarFilterContext<TFilters>,
  useViewFiltersMapper: () => (viewFilters: Partial<TApiFilters> | null) => TFilters,
  adaptFiltersToRequestFormat: (filters: TFilters) => TApiFilters,
}

interface IUseBondScreenerViewsProps<TApiFilters extends TBondScreenerFiltersShape> {
  type: TBondScreenerType,
  initialViews: IBondScreenerViewsResponse<TApiFilters>,
}

const createUseBondScreenerViews = <
  TFilters extends TBondScreenerFiltersShape,
  TApiFilters extends TBondScreenerFiltersShape = TFilters,
>({
  useSidebarFilterContext,
  useViewFiltersMapper,
  adaptFiltersToRequestFormat,
}: ICreateUseBondScreenerViewsOptions<TFilters, TApiFilters>) => (
  { type, initialViews }: IUseBondScreenerViewsProps<TApiFilters>,
) => {
  const {
    views, activeViewId, appliedViewId, setActiveViewId, commitActiveView, discardActiveView: discardActiveViewId,
    updateView, createView, renameView, deleteView, activateView,
  } = useBondScreenerViewsData<TApiFilters>({ type, initialViews });

  const { applyExternalColumns, resetColumns, discardColumns, getDraftColumns, applyColumns } =
    useColumnsCustomizationContext();
  const {
    resetTo: resetFiltersTo, reset: resetFilters, discard: discardFilters, getDraft: getDraftFilters,
    apply: applyFilters,
  } = useSidebarFilterContext();
  const mapViewFiltersToDraft = useViewFiltersMapper();

  // no re-apply: the table already shows the applied state, only the drafts drifted
  const discardActiveView = useCallback((): void => {
    discardColumns();
    discardFilters();
    discardActiveViewId();
  }, [discardColumns, discardFilters, discardActiveViewId]);

  const activeView = useMemo(
    () => views.find((view) => view.id === activeViewId) ?? null,
    [views, activeViewId],
  );

  const selectView = useCallback((viewId: string | null): void => {
    setActiveViewId(viewId);

    const view = viewId === null ? null : views.find((v) => v.id === viewId) ?? null;
    applyBondScreenerView(
      view,
      { applyExternalColumns, resetColumns, resetFiltersTo, resetFilters },
      mapViewFiltersToDraft,
    );
  }, [views, setActiveViewId, applyExternalColumns, resetColumns, resetFiltersTo, resetFilters, mapViewFiltersToDraft]);

  const selectAndApplyView = useCallback((viewId: string | null): void => {
    const isViewChanged = viewId !== activeViewId;

    selectView(viewId);
    applyColumns();
    applyFilters();
    commitActiveView(viewId);

    if (isViewChanged) {
      activateView(viewId);
    }
  }, [activeViewId, selectView, applyColumns, applyFilters, commitActiveView, activateView]);

  const saveActiveView = useCallback(async (): Promise<void> => {
    if (!activeView) return;

    const { columns, filters } = buildBondScreenerViewSavePayload({
      columns: getVisibleIds(getDraftColumns()),
      filters: getDraftFilters(),
    }, adaptFiltersToRequestFormat);

    await updateView(activeView.id, columns, filters);
  }, [activeView, getDraftColumns, getDraftFilters, updateView]);

  const saveAsNewView = useCallback(async (name: string): Promise<void> => {
    const { columns, filters } = buildBondScreenerViewSavePayload({
      columns: getVisibleIds(getDraftColumns()),
      filters: getDraftFilters(),
    }, adaptFiltersToRequestFormat);

    const created = await createView(name, columns, filters);
    if (created) {
      setActiveViewId(created.id);
      commitActiveView(created.id);
    }
  }, [getDraftColumns, getDraftFilters, createView, setActiveViewId, commitActiveView]);

  const renameActiveView = useCallback(async (name: string): Promise<void> => {
    if (!activeView) return;

    const isRenamed = await renameView(activeView.id, name);
    if (isRenamed) {
      applyColumns();
      applyFilters({ skipClose: true });
      commitActiveView(activeView.id);
    }
  }, [activeView, renameView, applyColumns, applyFilters, commitActiveView]);

  const deleteActiveView = useCallback(async (): Promise<void> => {
    if (!activeView) return;

    const isDeleted = await deleteView(activeView.id);
    if (!isDeleted) return;

    if (activeView.id === appliedViewId) {
      selectView(null);
      applyColumns();
      applyFilters({ skipClose: true });
      commitActiveView(null);
    } else {
      discardActiveView();
    }
  }, [
    activeView, appliedViewId, deleteView, selectView, applyColumns, applyFilters, commitActiveView,
    discardActiveView,
  ]);

  return useMemo(() => ({
    views,
    activeViewId,
    activeView,
    selectView,
    selectAndApplyView,
    commitActiveView,
    discardActiveView,
    saveActiveView,
    saveAsNewView,
    renameActiveView,
    deleteActiveView,
    canSaveActiveView: activeView !== null,
    canCreateView: views.length < MAX_BOND_SCREENER_VIEWS,
  }), [
    views, activeViewId, activeView, selectView, selectAndApplyView, commitActiveView,
    discardActiveView, saveActiveView, saveAsNewView, renameActiveView, deleteActiveView,
  ]);
};

export default createUseBondScreenerViews;
export type { IUseBondScreenerViewsProps, IViewsSidebarFilterContext };
